import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  ChevronDown,
  Lock,
  CheckCircle2,
  Circle,
  Layers,
  Pencil,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Course, CourseLesson } from '@/types/courses';

interface PlayerMobileTOCProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  course: Course;
  selectedLessonId: string | null;
  expandedModules: Set<string>;
  completedLessons: Record<string, boolean | string | null>;
  editMode?: boolean;
  onToggleModule: (moduleId: string) => void;
  onSelectLesson: (lesson: CourseLesson) => void;
  onToggleLessonComplete: (e: React.MouseEvent, lessonId: string) => void;
  // Admin-only handlers — only used when editMode is true.
  onEditLesson?: (lessonId: string) => void;
  onManageModules?: () => void;
}

export function PlayerMobileTOC({
  open,
  onOpenChange,
  course,
  selectedLessonId,
  expandedModules,
  completedLessons,
  editMode = false,
  onToggleModule,
  onSelectLesson,
  onToggleLessonComplete,
  onEditLesson,
  onManageModules,
}: PlayerMobileTOCProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[85vh] overflow-hidden flex flex-col">
        <SheetHeader className="border-b border-gray-200 dark:border-gray-700 pb-4">
          <SheetTitle>{course.title}</SheetTitle>
          <SheetDescription>Navigate through modules and lessons</SheetDescription>
          {editMode && onManageModules && (
            <Button
              type="button"
              variant="outline"
              onClick={() => onManageModules()}
              className="mt-3 w-full justify-center min-h-[44px]"
            >
              <Layers className="h-4 w-4 mr-2" />
              Manage Modules
            </Button>
          )}
        </SheetHeader>

        <nav className="flex-1 overflow-y-auto py-2">
          {course.modules.map((module) => {
            const isExpanded = expandedModules.has(module.id);
            return (
              <div
                key={module.id}
                className="border-b border-gray-100 dark:border-gray-800 last:border-b-0"
              >
                <button
                  onClick={() => onToggleModule(module.id)}
                  className="w-full flex items-center justify-between text-left px-4 py-3 text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-900"
                >
                  <span className="text-base font-medium">{module.title}</span>
                  <ChevronDown
                    className={cn(
                      'h-4 w-4 text-gray-500 transition-transform duration-200',
                      isExpanded && 'rotate-180'
                    )}
                  />
                </button>

                {isExpanded && (
                  <ul className="py-1 bg-gray-50 dark:bg-gray-900">
                    {module.lessons.map((lesson) => {
                      const isComingSoon = lesson.status === 'Coming Soon';
                      const showStatusBadge =
                        editMode && lesson.status !== 'Published';

                      // Non-admin coming-soon: locked, non-clickable.
                      if (isComingSoon && !editMode) {
                        return (
                          <li key={lesson.id} className="px-3 py-1">
                            <div className="flex items-center gap-2 px-3 py-2 rounded cursor-not-allowed text-gray-400 dark:text-gray-600">
                              <Lock className="h-4 w-4 flex-shrink-0" />
                              <span className="text-sm flex-1">{lesson.title}</span>
                              <Badge variant="outline" className="ml-auto text-xs">
                                Coming Soon
                              </Badge>
                            </div>
                          </li>
                        );
                      }
                      // Non-admin: hide non-published, non-coming-soon lessons.
                      if (!editMode && lesson.status !== 'Published') return null;
                      return (
                        <li key={lesson.id} className="px-3 py-1">
                          <div
                            className={cn(
                              'flex items-center gap-1 rounded',
                              selectedLessonId === lesson.id
                                ? 'bg-orange-50 dark:bg-orange-900/20'
                                : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                            )}
                          >
                            <button
                              onClick={() => {
                                onSelectLesson(lesson);
                                onOpenChange(false);
                              }}
                              className={cn(
                                'flex-1 text-left flex items-center gap-2 py-2 px-3 text-sm group',
                                selectedLessonId === lesson.id
                                  ? 'text-[#F37022] dark:text-orange-300 font-medium'
                                  : 'text-gray-700 dark:text-gray-300'
                              )}
                            >
                              <div
                                className="flex-shrink-0 cursor-pointer hover:scale-110 transition-transform z-10"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onToggleLessonComplete(e, lesson.id);
                                }}
                              >
                                {completedLessons[lesson.id] ? (
                                  <CheckCircle2 className="w-4 h-4 text-green-500 fill-green-100 dark:fill-green-900/30" />
                                ) : (
                                  <Circle className="w-4 h-4 text-gray-300 group-hover:text-gray-400" />
                                )}
                              </div>
                              <span className="flex-1">{lesson.title}</span>
                              {showStatusBadge && (
                                <Badge
                                  variant="outline"
                                  className="ml-auto text-xs shrink-0"
                                >
                                  {lesson.status}
                                </Badge>
                              )}
                            </button>
                            {editMode && onEditLesson && (
                              <button
                                type="button"
                                aria-label={`Edit lesson ${lesson.title}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onEditLesson(lesson.id);
                                }}
                                className="flex items-center justify-center min-w-[44px] min-h-[44px] text-gray-500 dark:text-gray-400 hover:text-[#F37022] dark:hover:text-orange-300 shrink-0"
                              >
                                <Pencil className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })}
        </nav>
      </SheetContent>
    </Sheet>
  );
}
